import { useCallback, useEffect, useState } from "react";
import { useLatest } from "../useLatest";
import type { UseAnchoredPositionArgs } from "./types";

export const useAnchoredPosition = <T>({
  isOpen,
  anchorRef,
  compute,
}: UseAnchoredPositionArgs<T>): T | null => {
  const [position, setPosition] = useState<T | null>(null);
  const computeRef = useLatest(compute);

  const recalculate = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPosition(computeRef.current(rect));
  }, [anchorRef, computeRef]);

  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }

    recalculate();

    // Reposition when the anchor itself resizes (e.g. an auto-growing textarea).
    const anchorElement = anchorRef.current;
    const resizeObserver = new ResizeObserver(recalculate);
    if (anchorElement) resizeObserver.observe(anchorElement);

    // Capture-phase scroll listener catches scrolls in any nested scrollable ancestor.
    window.addEventListener("scroll", recalculate, true);
    window.addEventListener("resize", recalculate);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("scroll", recalculate, true);
      window.removeEventListener("resize", recalculate);
    };
  }, [isOpen, recalculate, anchorRef]);

  return position;
};
